import type { ReactNode } from 'react';
import type { ImportDecision, PlanCandidate, PlanEntry } from '../../lib/types';

interface PlanDecisionProps {
  entry: PlanEntry;
  label: string;
  decision: ImportDecision | null;
  disabled: boolean;
  onDecide: (entryId: string, decision: ImportDecision) => void;
}

function candidateLabel(candidate: PlanCandidate): string {
  const version = candidate.version ? ` ${candidate.version}` : '';
  const vendor = candidate.vendor ? ` · ${candidate.vendor}` : '';
  return `Zuordnen: ${candidate.name}${version}${vendor}`;
}

/**
 * Several catalog entries fit the scanned component. There is deliberately no
 * default: the server refuses the apply until every such entry is decided.
 */
function CandidateDecision({ entry, label, decision, disabled, onDecide }: PlanDecisionProps): ReactNode {
  const candidates = entry.display?.candidates ?? [];
  return (
    <select
      className="imp-decision imp-candidate-select"
      aria-label={`Zuordnung für ${label}`}
      value={decision ?? ''}
      disabled={disabled}
      required
      onChange={(event) => {
        if (event.target.value !== '') onDecide(entry.entry_id, event.target.value as ImportDecision);
      }}
    >
      <option value="" disabled>Bitte entscheiden …</option>
      {candidates.map((candidate) => (
        <option key={candidate.key} value={`match:${candidate.key}`}>
          {candidateLabel(candidate)}
        </option>
      ))}
      <option value="incoming">Als neue Komponente anlegen</option>
    </select>
  );
}

/** Keeps every review choice tied to its original plan entry. */
export function PlanDecision(props: PlanDecisionProps): ReactNode {
  const { entry, label, decision, disabled, onDecide } = props;
  if (entry.action === 'unchanged') return null;
  if ((entry.display?.candidates?.length ?? 0) > 0) return <CandidateDecision {...props} />;
  const creating = entry.action === 'create';
  const removing = entry.action === 'remove';

  return (
    <div className="seg imp-decision" role="group" aria-label={`Entscheidung für ${label}`}>
      <button
        type="button"
        className={decision === 'incoming' ? 'on' : undefined}
        aria-pressed={decision === 'incoming'}
        title={
          removing
            ? 'Aus der Stückliste entfernen'
            : creating ? 'Neu anlegen' : 'Dateiwerte übernehmen'
        }
        disabled={disabled}
        onClick={() => onDecide(entry.entry_id, 'incoming')}
      >
        {removing ? 'Entfernen' : 'Übernehmen'}
      </button>
      <button
        type="button"
        className={decision === 'existing' ? 'on keep' : undefined}
        aria-pressed={decision === 'existing'}
        title={
          removing
            ? 'In der Stückliste behalten (der Scan hat die Komponente evtl. nicht erkannt)'
            : creating ? 'Diesen neuen Eintrag überspringen' : 'Bisherigen Stand behalten'
        }
        disabled={disabled}
        onClick={() => onDecide(entry.entry_id, 'existing')}
      >
        {creating ? 'Überspringen' : 'Behalten'}
      </button>
    </div>
  );
}
